import {resolvePolymorphVar} from "../../../functions/generic";
import {AppUtils} from "../../AppUtils";
import {ArrayUtils} from "../../ArrayUtils";
import {SortRules} from "../../SortRules";
import {ObjectUtils} from "../../ObjectUtils";
import {ErrorPresets} from "./ErrorPresets";
import {APIConnector} from "../APIConnector";

/**
 * @typedef {{
 *     error: String,
 *     message: String,
 *     statusCode: Number,
 *     time: Date,
 *     timestamp: Number,
 *     validationErrors?: Array<{
 *         field: String,
 *         message: String,
 *         constraint: String,
 *         errorValue: String,
 *     }>
 * }} HttpResponseError
 */

/**
 * @typedef {function(ResponseError, next: Function): void} ResponseErrorResolver
 */

export class ResponseError {
	//Core parts of the error
	statusCode;
	message;
	error;
	time;
	timestamp;
	//////////////////////////

	/**
	 * Original response data from the backend
	 * @type {{HttpResponseError}}
	 */
	#response;

	/**
	 * Whether the error was already processed by some handler
	 * @type {boolean}
	 */
	#wasResolved = false;

	/**
	 * A collection of handlers that should resolve this error based on a weighted last entry
	 * @type {[ResponseErrorResolver|{priority: number, resolver: ResponseErrorResolver}]}
	 */
	#errorResolvers = [];

	constructor(res, customInternalResponse = res) {
		this.time = new Date().toISOString();
		this.timestamp = Date.now();

		if(res instanceof Response) {
			this.#response = res.clone();

			const {error, message} = APIConnector.getErrorFromCode(this.statusCode);

			this.statusCode = res.status;
			this.message = res.statusText || message;
			this.error = error;
		}
		else {
			this.#response = customInternalResponse;
		}

		ObjectUtils.forEach(res, (key, value) => {
			this[key] = value;
		});
	}

	static get preset() {
		return ErrorPresets;
	}

	get response() {
		return this.#response;
	}

	markAsResolved() {
		this.#wasResolved = true;
	}

	addResolver(handler) {
		this.#errorResolvers.push(handler);

		return this;
	}

	resolve(markAsResolvedIfNoResolvers) {
		if(this.#wasResolved) return;

		if(this.#errorResolvers.length == 0) {
			if(markAsResolvedIfNoResolvers) {
				this.markAsResolved();
			}

			return;
		}

		const resolvers = ArrayUtils.mapValid(this.#errorResolvers, resolver => {
			return resolvePolymorphVar(
				resolver,
				{
					object: o => o,
					function: f => ({
						priority: 1,
						resolver: f,
					}),
				},
				() => {
					if(AppUtils.isDev) {
						console.error(`Unknown error resolver format`, resolver, this);
					}

					return undefined;
				},
				true
			);
		});

		const weightedResolvers = resolvers.sort((a, b) => SortRules.propertyAsc(a, b, "priority"));

		let resolverIndex = weightedResolvers.length - 1;
		const __next = () => {
			if(this.#wasResolved) return;

			resolverIndex--;

			if(resolverIndex >= 0) {
				weightedResolvers[resolverIndex].resolver(this, __next);
			}
		};

		__next();
	}

	/**
	 * @abstract
	 * Get localized data from error
	 * ---
	 * Returns a localized information about the error
	 * @returns {*}
	 */
	getLocalizedDataFromError() {
		return {
			title: this.error ?? this.#response?.error ?? "",
			message: this.message ?? this.#response?.message ?? "",
		}
	}
}

